import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState, } from 'react';
const SEGMENT_ANALYTICS_METHODS = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'screen',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
    'register',
];
const getExistingSegmentAnalytics = () => {
    if (typeof window === 'undefined') {
        return undefined;
    }
    return window.analytics;
};
export const resetSegmentAnalytics = () => {
    var _a, _b;
    (_b = (_a = getExistingSegmentAnalytics()) === null || _a === void 0 ? void 0 : _a.reset) === null || _b === void 0 ? void 0 : _b.call(_a);
};
export const loadSegmentAnalytics = (writeKey, options, initialPageProperties, trackInitialPage = true) => {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const existingAnalytics = getExistingSegmentAnalytics();
    if (!writeKey) {
        return (existingAnalytics === null || existingAnalytics === void 0 ? void 0 : existingAnalytics.track) ? existingAnalytics : undefined;
    }
    const analytics = (window.analytics =
        existingAnalytics !== null && existingAnalytics !== void 0 ? existingAnalytics : []);
    if (analytics.initialized || analytics._writeKey === writeKey) {
        return analytics;
    }
    if (analytics.invoked) {
        return analytics;
    }
    analytics.invoked = true;
    analytics.methods = SEGMENT_ANALYTICS_METHODS;
    analytics.factory = (method) => function segmentAnalyticsMethod(...methodArgs) {
        var _a, _b;
        if ((_a = window.analytics) === null || _a === void 0 ? void 0 : _a.initialized) {
            const initializedMethod = window.analytics[method];
            if (typeof initializedMethod === 'function') {
                initializedMethod.apply(window.analytics, methodArgs);
            }
            return window.analytics;
        }
        const args = [...methodArgs];
        if (['track', 'screen', 'alias', 'group', 'page', 'identify'].includes(method)) {
            const canonicalLink = document.querySelector("link[rel='canonical']");
            args.push({
                __t: 'bpc',
                c: (canonicalLink === null || canonicalLink === void 0 ? void 0 : canonicalLink.getAttribute('href')) || undefined,
                p: window.location.pathname,
                u: window.location.href,
                s: window.location.search,
                t: document.title,
                r: document.referrer,
            });
        }
        (_b = analytics.push) === null || _b === void 0 ? void 0 : _b.call(analytics, [method, ...args]);
        return analytics;
    };
    SEGMENT_ANALYTICS_METHODS.forEach((method) => {
        var _a;
        analytics[method] = (_a = analytics.factory) === null || _a === void 0 ? void 0 : _a.call(analytics, method);
    });
    analytics.load = (key, loadOptions) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.setAttribute('data-global-segment-analytics-key', 'analytics');
        script.src = `https://cdn.segment.com/analytics.js/v1/${key}/analytics.min.js`;
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript === null || firstScript === void 0 ? void 0 : firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        }
        else {
            document.head.appendChild(script);
        }
        analytics._loadOptions = loadOptions;
    };
    analytics._writeKey = writeKey;
    analytics.SNIPPET_VERSION = '5.2.0';
    analytics.load(writeKey, options);
    if (trackInitialPage) {
        if (initialPageProperties) {
            analytics.page(initialPageProperties);
        }
        else {
            analytics.page();
        }
    }
    return analytics;
};
export const useSegmentAnalytics = (writeKey, options, initialPageProperties, trackInitialPage = true) => {
    const initialised = useRef(false);
    const [analyticsInstance, setAnalyticsInstance] = useState(() => getExistingSegmentAnalytics());
    useEffect(() => {
        if (!writeKey) {
            setAnalyticsInstance(getExistingSegmentAnalytics());
            return;
        }
        if (initialised.current) {
            setAnalyticsInstance(getExistingSegmentAnalytics());
            return;
        }
        initialised.current = true;
        setAnalyticsInstance(loadSegmentAnalytics(writeKey, options, initialPageProperties, trackInitialPage));
    }, [writeKey, options, initialPageProperties, trackInitialPage]);
    return analyticsInstance;
};
const SegmentContext = createContext({
    analytics: undefined,
});
export const SegmentProvider = ({ writeKey, options, initialPageProperties, trackInitialPage, children, }) => {
    const analytics = useSegmentAnalytics(writeKey, options, initialPageProperties, trackInitialPage);
    return (_jsx(SegmentContext.Provider, { value: { analytics }, children: children }));
};
export const useSegment = () => {
    const context = useContext(SegmentContext);
    return context.analytics || getExistingSegmentAnalytics();
};
export default SegmentProvider;
